import { Panel } from "@kunduz/ui";
import { trackEvent } from "@kunduz/utility-analytics";
import { cleanWhitespace } from "../../../../common/services/utilityService";
import { useTranslation } from "next-i18next";
import { NextSeo, QAPageJsonLd } from "next-seo";
import Head from "next/head";
import { ReactElement, useEffect } from "react";
import { QISUGBreadcrumb } from "../../components/breadcrumb/sugBreadcrumb";
import SugPrevNextButtons from "../../components/prevNextButtons/sugPrevNext";
import styles from "./pageStyles.module.scss";
import {
  QuestionDate,
  QuestionImage,
  QuestionText,
  QuestionTitle,
} from "@kunduz/feature-question-indexing";
import { QuestionIndexing } from "@kunduz/data-access-qi-api";
import InstallComponent from "../../../../common/components/installComponent/loader";
import QuestionSideCtaLoader, {
  SideCTAModes,
} from "../../../../common/components/sideCta";
import { LastQuestionsSUG } from "../../components/lastQuestions/lastQuestionsSUG";
import { getQIUrl } from "@kunduz/utility-common";
import Link from "next/link";
import QuestionAnswerLoader from "../../components/answer/questionAnswerLoader";
import { WebsiteEnFooter, DynamicHeader } from "@kunduz/feature-landing-pages";
import Sticky from "../../../../common/components/sticky";
import { type IContent } from "@kunduz/data-access-cms-api";

export const SingleSUGQuestionPage = (props: Props): ReactElement => {
  const { t } = useTranslation("questionIndexing");

  const course = props.courses?.find((c) => c.id == props.question?.course_id);
  const subject = course?.subjects?.find(
    (s) => s.id == props.question?.subject_id,
  );
  const title = t("question_detail_meta_seo_title", {
    seoTitle: cleanWhitespace(props.question?.seo_title),
  });
  const description = t("question_detail_meta_description", {
    meta: cleanWhitespace(props.question.seo_meta).slice(0, 110),
  });
  const url = getQIUrl({
    lang: props.lang,
    domain: process.env.NEXT_PUBLIC_DOMAIN_PREFIX,
    questionSlug: props.question.slug,
    questionId: props.question.id,
  });

  function prepareQuestionImageUrl(url) {
    return `${url}?h=${512}`;
  }

  useEffect(() => {
    sendPageViewEvent();
  }, []);

  function sendPageViewEvent() {
    trackEvent("Question Indexing Question Detail Page Visited", {
      course: props.question.course,
      subject: props.question.subject,
      initial_answer_credit: t("question_indexing_answer_view_credit"),
    });
  }

  const sendInstallComponentClickedEvent = (
    buttonType: "app_store" | "google_play",
  ) => {
    trackEvent("Question Indexing Install CTA Clicked", {
      button_type: buttonType,
      source: "under_show_answer",
    });
  };

  /** prints QAPage Structured Content */
  function printStructuredContent() {
    return (
      <QAPageJsonLd
        mainEntity={{
          name: props.question?.header,
          url: url,
          text: props.question?.text_content,
          answerCount: 1,
          image: props.question?.image,
          dateCreated: props.question?.modified_at,
          author: {
            name: "Kunduz",
          },
          acceptedAnswer: {
            text: t("question_indexing_qa_page_accepted_answer_text"),
            dateCreated: props.question?.modified_at,
            url: url,
            author: {
              name: "Kunduz",
            },
          },
        }}
      />
    );
  }

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://media.kunduz.com" />
        <link rel="dns-prefetch" href="https://media.kunduz.com" />
        <link
          rel="preload"
          href={prepareQuestionImageUrl(props.question.image)}
          as="image"
        />
      </Head>

      <NextSeo
        title={title}
        description={description}
        openGraph={{
          url: props.url,
          images: [
            {
              url: prepareQuestionImageUrl(props.question.image),
              height: 512,
              alt: t("question_detail_meta_description", {
                meta: cleanWhitespace(props.question.seo_meta).slice(0, 110),
              }),
            },
          ],
          site_name: t("course_list_meta_title"),
        }}
        twitter={{
          handle: props.lang == "en" ? "@askkunduz" : "@kunduzasor",
          cardType: "summary_large_image",
        }}
        canonical={url}
        robotsProps={{
          maxImagePreview: "large",
        }}
      />
      {printStructuredContent()}

      <DynamicHeader
        lang={props.lang}
        headerType="student"
        headerItems={props.headerItems}
      />

      <div className={styles.pageContainer}>
        <div className={styles.breadcrumb}>
          <QISUGBreadcrumb
            question={props.question}
            lang={props.lang}
            courses={props.courses}
            linkAs={Link}
          />
        </div>

        <main className={styles.content}>
          <Panel style={{ position: "relative" }}>
            <QuestionTitle
              label={t("question_indexing_question_title")}
              title={props.question.seo_title}
            />
            <QuestionDate date={props.question.modified_at} />
            <QuestionImage
              imageUrl={props.question?.image}
              imageAlt={props.question?.seo_title}
            />
            <QuestionText questionText={props.question.text_content} />
          </Panel>

          <SugPrevNextButtons
            lang={props.lang}
            questionId={props.question.id}
            subjectId={subject?.id}
          />

          <QuestionAnswerLoader
            lang={props.lang}
            question={props.question}
            url={props.url}
            isAuth={props.isAuth}
          />

          <InstallComponent
            type="downloadApp"
            isMobile={props.isMobile}
            sendInstallComponentClickedEvent={sendInstallComponentClickedEvent}
          />

          <LastQuestionsSUG
            id={props.question.id}
            courseSlug={course?.slug}
            subjectSlug={subject?.slug}
            subjectName={subject?.name}
            courseId={props.question.course_id}
            subjectId={props.question.subject_id}
            isMobile={props.isMobile}
            lang={props.lang}
            linkAs={Link}
          />
        </main>

        <aside className={styles.sidebar} suppressHydrationWarning>
          <Sticky top={100}>
            <QuestionSideCtaLoader
              isMobile={props.isMobile}
              lang={props.lang}
              mode={SideCTAModes.QI}
              translationFile="questionIndexing"
            />
          </Sticky>
        </aside>
      </div>

      <WebsiteEnFooter />
    </>
  );
};

interface Props {
  question: QuestionIndexing.UsQiQuestion;
  lang: "tr" | "en";
  url: string;
  isAuth: boolean;
  isMobile: boolean;
  courses: QuestionIndexing.IQiCourse[];
  headerItems: IContent.HeaderItemsResponse;
}
